import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import { useAppState } from "main/context/app-context";
import { mapMenusToNavItems, NavItem } from "utils/menu-mapper";

import ArrowDown from "assets/icons/arrow-down.svg";

import {
  ExternalMark,
  GroupCard,
  GroupHeader,
  GroupTitle,
  IntroText,
  ItemCount,
  ItemLink,
  ItemsList,
  ToggleIcon,
  TreeContainer,
} from "./site-map.styles";

const INSTITUTIONAL_LINKS: NavItem[] = [
  { label: "Página Inicial", route: "/" },
  { label: "Notícias", route: "/noticias" },
  { label: "Busca no Portal", route: "/busca" },
  { label: "Acessibilidade", route: "/acessibilidade" },
  { label: "Política de Privacidade (LGPD)", route: "/pagina/lgpd" },
];

interface SiteMapGroupProps {
  item: NavItem;
}

const SiteMapGroup: React.FC<SiteMapGroupProps> = ({ item }) => {
  const navigate = useNavigate();
  const hasChildren = !!item.items && item.items.length > 0;
  const [expanded, setExpanded] = useState(false);

  const handleNavigate = (target: NavItem) => {
    if (target.isExternal) {
      window.open(target.route, "_blank", "noopener,noreferrer");
    } else {
      navigate(target.route);
    }
  };

  const handleHeaderClick = () => {
    if (hasChildren) {
      setExpanded((prev) => !prev);
    } else {
      handleNavigate(item);
    }
  };

  return (
    <GroupCard>
      <GroupHeader $expandable={hasChildren}>
        <GroupTitle
          onClick={handleHeaderClick}
          aria-expanded={hasChildren ? expanded : undefined}
        >
          {hasChildren && (
            <ToggleIcon
              src={ArrowDown}
              alt=""
              aria-hidden="true"
              $expanded={expanded}
            />
          )}
          {item.label}
        </GroupTitle>

        {hasChildren && (
          <ItemCount>
            {item.items!.length} {item.items!.length === 1 ? "item" : "itens"}
          </ItemCount>
        )}
      </GroupHeader>

      {hasChildren && expanded && (
        <ItemsList>
          {item.items!.map((child) => (
            <li key={child.label}>
              <ItemLink
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigate(child);
                }}
                href={child.route}
                target={child.isExternal ? "_blank" : undefined}
                rel={child.isExternal ? "noopener noreferrer" : undefined}
              >
                {child.label}
                {child.isExternal && <ExternalMark>(link externo)</ExternalMark>}
              </ItemLink>
            </li>
          ))}
        </ItemsList>
      )}
    </GroupCard>
  );
};

export function SiteMapPage() {
  const { menus, menusError } = useAppState();

  const menuItems: NavItem[] = useMemo(() => {
    if (menusError || !menus || menus.length === 0) {
      return [];
    }
    return mapMenusToNavItems(menus);
  }, [menus, menusError]);

  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Mapa do Site" },
        ]}
      />

      <main id="content">
        <PageTitle>Mapa do Site</PageTitle>

        <IntroText>
          Esta página reúne, de forma organizada, todas as seções e
          conteúdos disponíveis neste portal. Clique sobre um item com
          seta para expandir suas subseções, ou clique diretamente
          sobre um link para acessar o conteúdo.
        </IntroText>

        <TreeContainer>
          {menuItems.map((item) => (
            <SiteMapGroup key={item.label} item={item} />
          ))}

          <SiteMapGroup
            item={{ label: "Institucional", route: "#", items: INSTITUTIONAL_LINKS }}
          />
        </TreeContainer>
      </main>
    </>
  );
}
